import type { FieldDefinition, IntegrationProviderConfig } from '../../types';
import { isZapierWebhookUrlValid } from './zapierValidation';
import ZapierIcon from './ZapierIcon';
import ZapierPullPanelExtras from './ZapierPullPanelExtras';
import ZapierPushPanelExtras from './ZapierPushPanelExtras';

const pullObjectTypes = [
  { label: 'Webhook Listener', value: 'webhook_listener' },
  { label: 'Poll Zapier Endpoint', value: 'poll_endpoint' },
];

const payloadFormatOptions = [
  { label: 'JSON', value: 'json' },
  { label: 'Form Data', value: 'form' },
];

function getPullFields(objectType: string): FieldDefinition[] {
  if (objectType === 'webhook_listener') {
    return [
      {
        name: 'webhookUrl',
        label: 'Webhook URL (copy to Zapier)',
        type: 'text',
        placeholder: 'Generated from your node id',
        readOnly: true,
      },
    ];
  }

  if (objectType === 'poll_endpoint') {
    return [
      {
        name: 'zapierEndpointUrl',
        label: 'Zapier Endpoint URL',
        type: 'text',
        placeholder: 'URL of Zapier webhook to poll for data',
        validation: { required: true },
      },
      {
        name: 'limit',
        label: 'Limit',
        type: 'number',
        defaultValue: 1,
        placeholder: 'Max items to return (default 1)',
      },
    ];
  }

  return [];
}

const pushActions = [
  { label: 'Send to webhook', value: 'send_to_webhook' },
];

function getPushFields(action: string): FieldDefinition[] {
  if (action === 'send_to_webhook') {
    return [
      {
        name: 'webhookUrl',
        label: 'Zapier Webhook URL',
        type: 'text',
        placeholder: 'Paste your Zapier Catch Hook URL (e.g. https://hooks.zapier.com/...)',
        validation: { required: true },
      },
      {
        name: 'payloadFormat',
        label: 'Payload format',
        type: 'select',
        options: payloadFormatOptions,
        defaultValue: 'json',
      },
      {
        name: 'payload',
        label: 'Payload',
        type: 'textarea',
        placeholder: 'JSON or text to send. Connect an edge to use output from another node.',
        validation: { required: true },
        hasConnectionPoint: true,
      },
      {
        name: 'customHeaders',
        label: 'Custom headers (optional)',
        type: 'textarea',
        placeholder: 'One per line: Header-Name: value',
        hasConnectionPoint: true,
      },
    ];
  }

  return [];
}

export const zapierConfig: IntegrationProviderConfig = {
  id: 'zapier',
  name: 'Zapier',
  icon: ZapierIcon,
  brandColor: '#FF4A00',
  authMethod: 'both',
  authFields: [
    {
      name: 'apiKey',
      label: 'API Key (Zapier App mode only)',
      type: 'text',
      placeholder: 'Optional — leave empty for Webhook mode',
      hasConnectionPoint: true,
    },
  ],
  pullConfig: {
    objectTypes: pullObjectTypes,
    getFields: getPullFields,
    resolveReadOnlyValues: (ctx) =>
      ctx.selectedObject === 'webhook_listener'
        ? {
            webhookUrl: `${ctx.apiBase.replace(/\/$/, '')}/webhooks/zapier/${ctx.nodeId}`,
          }
        : {},
    objectTypeHelpCaption: (objectType) =>
      objectType === 'webhook_listener'
        ? 'Copy the Webhook URL into your Zapier trigger. Use "Load last received" to see the latest payload.'
        : 'Enter the URL of a Zapier webhook or endpoint to poll for data.',
    supportsLoadLastReceived: (selectedObject) => selectedObject === 'webhook_listener',
    PullPanelExtras: ZapierPullPanelExtras,
  },
  pushConfig: {
    actions: pushActions,
    getFields: getPushFields,
    validateWebhookUrl: (url) => ({
      valid: isZapierWebhookUrlValid(url),
      errorMessage: 'URL must start with https://hooks.zapier.com/',
    }),
    supportsTestPush: true,
    PushPanelExtras: ZapierPushPanelExtras,
  },
  supportsPull: true,
  supportsPush: true,
};
